import { useEffect, useRef, useState } from "react";
import { startOfDay } from "date-fns";
import type {
  AnalyticsAggregateRow,
  SegmentDefinition,
} from "@adlywise/shared";
import { requestAggregate } from "@/features/analytics/aggregateApi";
import { REQUEST_CANCEL_VISIBILITY_DELAY_MS } from "@/features/analytics/constants";
import type { AnalyticsConfig } from "@/features/analytics/components/ConfigurePanel";

type PickerRange = { from: Date; to: Date };

type UseAnalyticsAggregateParams = {
  config: AnalyticsConfig;
  range: PickerRange;
  filter: SegmentDefinition;
};

type UseAnalyticsAggregateResult = {
  rows: AnalyticsAggregateRow[];
  truncated: boolean;
  hasRun: boolean;
  isLoading: boolean;
  isRequestCancelable: boolean;
  isRunReportButtonDisabled: boolean;
  runReportButtonLabel: string;
  errorMessage: string;
  handleRunReportButtonClick: () => void;
};

// The dashboard DateRangePicker yields Kyiv wall-clock day starts (the browser
// runs Kyiv-local). The backend range.ts reads the Kyiv CALENDAR DAY of each
// instant and derives the exact Kyiv-midnight UTC window itself (to exclusive),
// so we only need to hand it an instant that falls on the right Kyiv day — the
// start-of-day instant does exactly that.
const toRangeInstants = (
  range: PickerRange,
): { from: string; to: string } => ({
  from: startOfDay(range.from).toISOString(),
  to: startOfDay(range.to).toISOString(),
});

export const useAnalyticsAggregate = ({
  config,
  range,
  filter,
}: UseAnalyticsAggregateParams): UseAnalyticsAggregateResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [isRequestCancelable, setIsRequestCancelable] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [rows, setRows] = useState<AnalyticsAggregateRow[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const requestAbortControllerRef = useRef<AbortController | null>(null);
  const cancelVisibilityTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (cancelVisibilityTimeoutRef.current !== null) {
        window.clearTimeout(cancelVisibilityTimeoutRef.current);
      }
      requestAbortControllerRef.current?.abort();
    };
  }, []);

  const clearCancelVisibilityTimeout = (): void => {
    if (cancelVisibilityTimeoutRef.current !== null) {
      window.clearTimeout(cancelVisibilityTimeoutRef.current);
      cancelVisibilityTimeoutRef.current = null;
    }
  };

  const handleRunReport = async (): Promise<void> => {
    const abortController = new AbortController();

    requestAbortControllerRef.current = abortController;
    setIsLoading(true);
    setIsRequestCancelable(false);
    setErrorMessage("");

    cancelVisibilityTimeoutRef.current = window.setTimeout(() => {
      if (requestAbortControllerRef.current === abortController) {
        setIsRequestCancelable(true);
      }
    }, REQUEST_CANCEL_VISIBILITY_DELAY_MS);

    try {
      const response = await requestAggregate(
        {
          source: config.source,
          groupBy: config.groupBy,
          bucket: config.bucket,
          range: toRangeInstants(range),
          // Include-only / AND-locked: the drawer only ever writes the "all"
          // group. Send just that so `any`/`none` never leak into the request.
          filter: { all: filter.all ?? [] },
          // statuses are responses-only (each selected status → one metric
          // group). Omit entirely on requests (fixed `requests` metric group).
          ...(config.source === "responses"
            ? { statuses: config.statuses }
            : {}),
        },
        { signal: abortController.signal },
      );

      setRows(response.rows);
      setTruncated(response.truncated);
      setHasRun(true);
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return;
      }

      setRows([]);
      setTruncated(false);
      setHasRun(true);
      setErrorMessage("Request failed. Check browser console and network tab.");
      console.error("analytics/aggregate request failed", error);
    } finally {
      clearCancelVisibilityTimeout();

      if (requestAbortControllerRef.current === abortController) {
        requestAbortControllerRef.current = null;
        setIsLoading(false);
        setIsRequestCancelable(false);
      }
    }
  };

  const handleCancelRequest = (): void => {
    clearCancelVisibilityTimeout();
    requestAbortControllerRef.current?.abort();
    requestAbortControllerRef.current = null;
    setIsLoading(false);
    setIsRequestCancelable(false);
  };

  const handleRunReportButtonClick = (): void => {
    if (isLoading && isRequestCancelable) {
      handleCancelRequest();
      return;
    }

    if (isLoading) {
      return;
    }

    void handleRunReport();
  };

  return {
    rows,
    truncated,
    hasRun,
    isLoading,
    isRequestCancelable,
    isRunReportButtonDisabled: isLoading && !isRequestCancelable,
    runReportButtonLabel:
      isLoading && isRequestCancelable ? "Cancel request" : "Run report",
    errorMessage,
    handleRunReportButtonClick,
  };
};
